import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from "react";
import type { ReactNode } from "react";
import ErrorDisplayHTML from "../components/ErrorDisplay/ErrorDisplayHTML";
import { normalizeApiError } from "../api/normalizeApiError";
import type { AppError } from "../models/AppError";

interface ErrorContextState {
  error?: AppError;
}

interface ErrorContextValue {
  error?: AppError;
  showError: (error: AppError) => void;
  handleError: (error: unknown) => void;
  closeError: () => void;
}

interface ErrorProviderProps {
  children: ReactNode;
}

const ErrorContext = createContext<ErrorContextValue | undefined>(undefined);

export function ErrorProvider({ children }: ErrorProviderProps) {
  const [state, setState] = useState<ErrorContextState>({
    error: undefined,
  });

  const showError = useCallback((error: AppError): void => {
    setState({ error });
  }, []);

  const handleError = useCallback((error: unknown): void => {
    setState({ error: normalizeApiError(error) });
  }, []);

  const closeError = useCallback((): void => {
    setState({ error: undefined });
  }, []);

  const value = useMemo<ErrorContextValue>(
    () => ({
      error: state.error,
      showError,
      handleError,
      closeError,
    }),
    [state.error, showError, handleError, closeError],
  );

  return (
    <ErrorContext.Provider value={value}>
      {children}
      <ErrorDisplayHTML error={state.error} onClose={closeError} />
    </ErrorContext.Provider>
  );
}

export function useError(): ErrorContextValue {
  const context = useContext(ErrorContext);

  if (!context) {
    throw new Error("useError must be used inside ErrorProvider.");
  }

  return context;
}
